/**
 * The acceptance wizard's steps, as data, for any profession.
 *
 * WHY THIS EXISTS (spec 42ec3c57, slice 1). The two acceptance flows are not
 * one wizard with a different first step — they are different wizards:
 *
 *   notary      Identity, Phone, Profile, Coverage, Billing, Password
 *   accountant  Identity, Certificate, Fiscal, Details
 *
 * Different counts, different sets. That is invisible while one is declarative
 * (notary-invite-steps.ts) and the other is an array built inline halfway down
 * a 1,494-line component, so the first thing this module buys is that the two
 * can be read side by side. Composing them comes next, and cannot be designed
 * honestly until they are comparable.
 *
 * THE GATE IS OPTIONAL, AND THAT IS A REAL DIVERGENCE RATHER THAN AN OVERSIGHT.
 * The notary's stepper marks a step complete only when the applicant is past it
 * AND its gate is satisfied; the accountant's marks it complete on the index
 * alone, because — in its own words — "each step is left only by its own footer
 * button, which enforces the real gate". Both are defensible and they are not
 * the same rule. Collapsing them here would be a behaviour change smuggled into
 * a refactor whose whole claim is that it changes nothing, so the difference is
 * carried as data: supply a gate and you get the notary's semantics, omit one
 * and you get the accountant's. Settling which is right is RES-1 of the spec.
 */

export interface ProfessionalStepDefinition<TState> {
  /** 1-based, and the order the applicant meets them in. */
  step: number;
  /** i18n key for the stepper's label — short; it sits under a 32px circle. */
  labelKey: string;
  /**
   * Satisfied means "this step may be left", nothing more.
   *
   * OMITTED means the step is complete as soon as the applicant is past it.
   * See the note above: that is the accountant flow's existing rule, not a
   * missing gate.
   */
  gate?: (state: TState) => boolean;
}

export interface ProfessionalStepperEntry {
  label: string;
  complete: boolean;
}

/**
 * The stepper's entries, from the SAME list the wizard navigates.
 *
 * One list, two readers — which is the property notary-invite-steps.spec.ts
 * already holds for its flow: if each computed its own answer, the stepper
 * would sooner or later announce a step the button refuses to leave.
 *
 * The index half — `step` has moved past this one — is what keeps the marker
 * on the step actually on screen when the applicant walks BACK, because
 * OnboardingStepper derives "current" as the first incomplete entry.
 */
export function professionalStepperEntries<TState>(
  definitions: readonly ProfessionalStepDefinition<TState>[],
  translate: (key: string) => string,
  current: number,
  state: TState,
): ProfessionalStepperEntry[] {
  return definitions.map((definition) => ({
    label: translate(definition.labelKey),
    complete: definition.step < current && (definition.gate?.(state) ?? true),
  }));
}

/**
 * A flow's steps as stepper definitions, numbered by their position in it.
 *
 * REPLACES A SECOND HAND-WRITTEN LIST. The accountant's four steps were
 * declared here as well as in its flow, which is two sources of truth about
 * one wizard — and the kind the C-3 probe already caught once, where a guard
 * compared two of my own constructs. The flow is the source; this derives.
 */
export function stepperDefinitionsForFlow(
  flow: readonly { labelKey: string }[],
): readonly ProfessionalStepDefinition<unknown>[] {
  return flow.map((entry, index) => ({ step: index + 1, labelKey: entry.labelKey }));
}

/**
 * Which 1-based step asks about a concern, or null when the flow never does.
 *
 * SO THAT NOTHING HARDCODES A STEP NUMBER. The accountant page sent a
 * half-finished signup to "step 3" meaning the fiscal declaration and "step
 * 4" meaning the end; both moved when the tail stopped being bundled, and a
 * literal 3 in a hydration branch would have put the applicant on a step
 * about something else entirely with no error anywhere.
 */
export function stepNumberForConcern(
  flow: readonly { concerns: readonly Concern[] }[],
  concern: Concern,
): number | null {
  const index = flow.findIndex((entry) => entry.concerns.includes(concern));
  return index === -1 ? null : index + 1;
}

/* ────────────────────────────────────────────────────────────────────────────
   COMPOSITION (spec 42ec3c57, slice 2)
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * What the applicant has to supply, as stable keys.
 *
 * A STRATEGY CONTRIBUTES REQUIREMENTS, NOT STEPS, and that is the whole reason
 * composition works. Every strategy wants the cédula; a union taken over steps
 * would ask for it once per strategy, which reads as a broken form and is the
 * fastest way to lose an applicant mid-signup. Taken over distinct keys, a
 * profession requiring document_lookup AND manual_review asks for it once.
 */
export type RequirementKey =
  | "identity.cedula"
  | "identity.credential"
  | "identity.names"
  | "document.certificate";

/**
 * Which requirements each implemented strategy declares.
 *
 * MUST COVER EVERY STRATEGY THE BACKEND IMPLEMENTS. A strategy present there
 * and absent here is configurable by an admin and unaskable by the wizard —
 * the applicant would meet a step with nothing in it, which is C-6.
 */
export const STRATEGY_REQUIREMENTS: Record<string, readonly RequirementKey[]> = {
  poder_judicial: ["identity.cedula", "identity.credential"],
  document_lookup: ["identity.cedula", "identity.names", "document.certificate"],
  manual_review: ["identity.cedula", "identity.names"],
};

/**
 * The four parts an identity document records, as a register returns them.
 *
 * LIVES HERE AND NOT IN THE SECTION because the GATE is the part worth
 * testing, and a gate declared next to its JSX cannot be run by the node
 * specs. The section imports this type; the predicate below is what the page
 * and its footer button both read.
 */
export interface IdentityNames {
  firstName: string;
  secondFirstName: string;
  firstLastName: string;
  secondLastName: string;
}

/** What the register lookup has so far managed to say about a cedula. */
export interface IdentityNamesLookup {
  /** A request is in flight. */
  loading: boolean;
  /** The register answered and the names below came from it. */
  ready: boolean;
  /** The register could not answer, so the applicant may type them. */
  failed: boolean;
}

/**
 * May an identity step that asked for `identity.names` be left?
 *
 * ONE EXPRESSION, TWO READERS — the submit handler and the footer button.
 * They were five terms written out twice on the accountant page, which is
 * the shape FM-2 warns about: a button that offers to continue past a check
 * the handler then refuses, or worse, the other way round.
 *
 * THE SECOND SURNAME IS NOT REQUIRED and the second given name is not
 * either, because plenty of people have neither. Only the first of each is
 * demanded, which is also what the server's manual-names fallback requires.
 *
 * MANUAL ENTRY COUNTS ONLY WHEN THE LOOKUP ACTUALLY FAILED. Typed names are
 * accepted as a substitute for a register that could not answer — not as an
 * override of one that did, which would let an applicant rename the person
 * the register identified.
 */
export function identityNamesSatisfied(
  lookup: IdentityNamesLookup,
  names: IdentityNames,
): boolean {
  if (lookup.loading) return false;
  if (lookup.ready) return true;
  return (
    lookup.failed &&
    names.firstName.trim().length > 0 &&
    names.firstLastName.trim().length > 0
  );
}

/**
 * The concerns a signup is made of, in the order they are met.
 *
 * SEPARATE FROM STEPS, because the two existing flows bundle the same concerns
 * differently: the notary gives phone, coverage and password a step each, and
 * the accountant folds all three into its final "Tu perfil". Neither is wrong;
 * they are the same signup cut at different places. Keying on the concern is
 * what lets a union dedupe at all — on steps, "Facturación" and "stepFiscal"
 * look like two different things.
 */
export type Concern =
  | "identity"
  | "certificate"
  | "phone"
  | "profile"
  | "coverage"
  | "billing"
  | "password";

/**
 * Which concern each requirement is asked under.
 *
 * EXPORTED FOR THE GUARD, not just for the composer: a requirement's concern
 * is what says which SECTION component has to be able to render it, and
 * acceptance-composition.spec.ts reads it to check that the section actually
 * does (FM-1).
 */
export const CONCERN_FOR_REQUIREMENT: Record<RequirementKey, Concern> = {
  "identity.cedula": "identity",
  "identity.credential": "identity",
  "identity.names": "identity",
  "document.certificate": "certificate",
};

/**
 * The concerns every signup needs regardless of how it is verified.
 *
 * Verification decides `identity` and `certificate`; these are the rest of
 * becoming a professional on the platform, and no strategy owns them.
 */
const SIGNUP_TAIL: readonly Concern[] = [
  "phone",
  "profile",
  "coverage",
  "billing",
  "password",
];

/** Canonical order, used when a profession's bundling is not already decided. */
export const CANONICAL_CONCERNS: readonly Concern[] = [
  "identity",
  "certificate",
  ...SIGNUP_TAIL,
];

/** The signup tail's concerns — every one a professional meets whoever they are. */
export type TailConcern = Exclude<Concern, "identity" | "certificate">;

/**
 * What the tail reads to decide whether a step may be left.
 *
 * ONE SHAPE FOR EVERY FLOW. Both pages already held all of it under these
 * names; writing it down as a type is what lets the gates below be shared
 * rather than re-derived per profession.
 */
export interface SignupTailState {
  phoneVerified: boolean;
  profileImageFile: File | null;
  fiscalRut: string;
  fiscalRazonSocial: string;
  fiscalIssuesCfe: boolean | null;
  /**
   * The server ALREADY HOLDS this declaration, so the local form is
   * legitimately empty and must not be demanded.
   *
   * Only the document-lookup flow can be in this state — an accountant's user
   * row commonly exists before acceptance — and it is the ordinary case there,
   * not an edge one. Absent means "no such claim", which is the notary's
   * situation and leaves the three checks in force.
   */
  fiscalAlreadyDeclared?: boolean;
  password: string;
  confirmPassword: string;
}

/**
 * May a tail step be left? The same answer for every profession.
 *
 * SHARED BY CONSTRUCTION, NOT BY AGREEMENT. The notary's gates and the
 * accountant's footer conditions were separate expressions that happened to
 * coincide on three concerns and diverge on two — the accountant demanded a
 * verified phone on the step that submitted, and never gated the photo at
 * all, though its handleSubmit refused without one. Delegating both to this
 * function removes the possibility of drift instead of testing for it.
 *
 * COVERAGE IS DELIBERATELY UNGATED. An empty selection is a legitimate
 * answer that shows every request unranked; blocking on it manufactures
 * coverage people do not serve, which is worse than nothing because it looks
 * like data (FM-3).
 */
export function tailConcernSatisfied(
  concern: TailConcern,
  state: SignupTailState,
): boolean {
  switch (concern) {
    case "phone":
      return state.phoneVerified;
    case "profile":
      return state.profileImageFile != null;
    case "coverage":
      return true;
    case "billing":
      return (
        state.fiscalAlreadyDeclared === true ||
        (state.fiscalRut.replace(/\D/g, "").length === 12 &&
          state.fiscalRazonSocial.trim().length >= 2 &&
          state.fiscalIssuesCfe === true)
      );
    case "password":
      return state.password.length >= 8 && state.password === state.confirmPassword;
  }
}

export interface ComposedStep {
  /** The concerns this step covers — more than one when a flow bundles them. */
  concerns: readonly Concern[];
  labelKey: string;
  /** Every requirement asked on this step, deduplicated. */
  requirements: readonly RequirementKey[];
}

/**
 * Which copy names a refusal the acceptance flow can receive, or null when
 * this client has nothing of its own to say about it.
 *
 * THE PORTAL OWNS THE WORDS ON THIS ROUTE. The acceptance page is
 * unauthenticated and carries its own language toggle, which is why the
 * server serves a copyNamespace rather than prose — "THE NAMESPACE, NOT THE
 * WORDS", as getProfessionalInviteByToken puts it. The same reasoning applies
 * to its REFUSALS, and did not used to: the page preferred the server's
 * message whenever there was one, so a traductor publico who mistyped a
 * matricula was told it "no corresponde a un escribano habilitado" — the word
 * belonging to whichever profession the register path was first written for.
 *
 * SO A RECOGNISED CODE USES OUR COPY, which is parameterised by
 * professionCopy and can name the profession correctly. An UNRECOGNISED one
 * still falls back to the server's message, because there the server is
 * saying something this client does not know how to say at all — and a
 * specific sentence in the wrong register beats a generic one.
 */
export function acceptanceRefusalKey(code: string | undefined): string | null {
  switch (code) {
    case "notary_not_enabled":
      return "auth.acceptProfessionalInvitation.notEnabled";
    case "cedula_not_found":
      return "auth.acceptProfessionalInvitation.cedulaNotFound";
    case "matricula_invalid_or_mismatch":
      return "auth.acceptProfessionalInvitation.credentialMismatch";
    case "poder_judicial_unavailable":
    case "poder_judicial_error":
      return "auth.acceptProfessionalInvitation.validationUnavailable";
    case "accountant_invite_identity_mismatch":
      return "auth.acceptProfessionalInvitation.detailMismatch";
    case "certificate_required":
      return "auth.acceptProfessionalInvitation.certificateRequired";
    case "phone_not_verified":
      return "auth.acceptProfessionalInvitation.phoneVerifyFirst";
    case "invalid_phone":
      return "auth.acceptProfessionalInvitation.phoneInvalid";
    case "accountant_manual_review_declined":
    case "manual_review_declined":
      return "auth.acceptProfessionalInvitation.manualReviewDeclined";
    case "invalid_certificate_type":
      return "auth.acceptProfessionalInvitation.certificateInvalidType";
    case "certificate_too_large":
      return "auth.acceptProfessionalInvitation.certificateTooBig";
    default:
      return null;
  }
}

/**
 * WHICH SOURCE SHOULD SPEAK for a refusal — ours or the server's.
 *
 * A FUNCTION RATHER THAN A LINE IN THE PAGE, because the precedence IS the
 * bug. It used to be `backendMessage ?? t(key)`: the server's sentence won
 * whenever there was one, so the page's parameterised copy never ran and a
 * traductor publico was told their matricula does not belong to an escribano.
 * Written inline, reversing it again is a one-token edit that no test could
 * see; written here, the reversal fails a case.
 *
 * `{ key }` means WE say it, with professionCopy interpolated.
 * `{ message }` means the server does, because we have nothing for that code.
 */
export function refusalSource(
  code: string | undefined,
  backendMessage: string | null,
): { key: string } | { message: string | null } {
  const key = acceptanceRefusalKey(code);
  return key ? { key } : { message: backendMessage };
}

/**
 * The refusals whose copy MUST be able to name the profession.
 *
 * Each of these is about a credential or a register, and a register is a
 * profession's own — so a message that cannot interpolate is a message that
 * hardcodes somebody else's noun. That is the bug this list exists to stop
 * coming back: the server's version of the first one says "escribano" to
 * every profession that walks the register path.
 */
export const PROFESSION_NAMING_REFUSALS: readonly string[] = [
  "matricula_invalid_or_mismatch",
  "notary_not_enabled",
];

/** What sessionStorage holds for a half-finished signup, for any profession. */
export interface ComposedInviteProgress {
  firstName: string;
  lastName: string;
  cedula: string;
  /** Only a register-verified profession has one. */
  credential?: string;
  /** The furthest step reached — not the one on screen. */
  step?: number;
  /** Carried so the phone gate survives a reload; the server re-checks it. */
  phone?: string;
  phoneVerified?: boolean;
}

/**
 * Where a restored applicant lands, for any flow.
 *
 * A STORED STEP IS HONOURED ONLY AS FAR AS THE STATE THAT SURVIVES A RELOAD
 * ALLOWS, and what survives is the same for every profession: the validated
 * identity and the phone verification are written down, and the profile photo
 * is a File that cannot be. So the ceiling is the PHOTO's step when the phone
 * came back verified, and the phone's step when it did not. Restoring past it
 * puts the applicant at a submit that refuses on a field several screens back
 * — FM-1 arriving by the back door.
 *
 * THE FLOOR IS THE FIRST TAIL STEP, because a payload exists only once the
 * identity was confirmed: there is nothing to restore before that, and a
 * payload with no `step` at all is one an older build wrote, which has always
 * meant "the identity is confirmed, carry on from there".
 *
 * DERIVED FROM THE FLOW, not from numbers. This replaces restoreNotaryStep,
 * whose 2 and 3 were the phone and the photo in a six-step wizard — correct
 * there and meaningless in a flow with a certificate step in front of them.
 */
export function restoreComposedStep(
  flow: readonly { concerns: readonly Concern[] }[],
  progress: ComposedInviteProgress,
): number {
  const firstTail = stepNumberForConcern(flow, "phone") ?? flow.length;
  const ceiling = progress.phoneVerified
    ? (stepNumberForConcern(flow, "profile") ?? firstTail)
    : firstTail;
  if (progress.step == null) return firstTail;
  const stored = Math.floor(progress.step);
  if (!Number.isFinite(stored) || stored < firstTail) return firstTail;
  return stored < ceiling ? stored : ceiling;
}

/** What the server says this invitation still owes. */
export interface InviteStatusFacts {
  certificateUploaded: boolean;
  fiscalDeclared: boolean;
  manualApprovalStatus: "pending" | "approved" | "declined" | null;
}

/** Everything a restored signup needs applied, decided in one place. */
export interface RestoredSignup {
  /** True when the human review refused: nothing else matters. */
  declined: boolean;
  /** Null when there is nothing stored to restore. */
  step: number | null;
  certificateUploaded: boolean;
  fiscalAlreadyDeclared: boolean;
}

/**
 * WHERE A RELOADED TAB LANDS AND WHAT IT KNOWS — as a function of the flow,
 * the stored progress and the server's facts, and nothing else.
 *
 * EXTRACTED BECAUSE IT WAS THE ONE PART OF THE PAGE THAT COULD BE WRONG
 * SILENTLY. Every other decision in an acceptance wizard is visible the moment
 * you look at it; this one quietly resumes someone on the wrong step, and the
 * case that matters strands them past the phone gate at a submit refusing on
 * it. It lived inside a useEffect alongside six setters and two awaits, where
 * nothing could call it with a made-up payload and check the answer.
 *
 * THE ORDER OF THE RULES IS THE WHOLE CONTENT:
 *
 *  1. A declined review ends it. The invitation cannot be completed and no
 *     step is worth computing.
 *  2. A CERTIFICATE STILL OWED OUTRANKS THE STORED STEP, because it is a
 *     server fact rather than a local one and nothing after it can be
 *     finished without it.
 *  3. Otherwise the stored step, capped by what survives a reload —
 *     restoreComposedStep's rule.
 *
 * `fiscalAlreadyDeclared` is carried rather than acted on: it is what stops
 * the billing step demanding a declaration the server already holds, and it
 * must NOT be used to position the applicant. Positioning by it is the bug
 * this extraction was written to make visible — the page it came from sent a
 * returning applicant to the fiscal step, which worked only while the tail
 * sat behind it.
 */
export function restoreSignup(args: {
  flow: readonly { concerns: readonly Concern[] }[];
  progress: ComposedInviteProgress | null;
  status: InviteStatusFacts | null;
  wantsCertificate: boolean;
}): RestoredSignup {
  const { flow, progress, status, wantsCertificate } = args;

  if (status?.manualApprovalStatus === "declined") {
    return {
      declined: true,
      step: null,
      certificateUploaded: false,
      fiscalAlreadyDeclared: false,
    };
  }

  const facts = {
    declined: false,
    certificateUploaded: status?.certificateUploaded === true,
    fiscalAlreadyDeclared: status?.fiscalDeclared === true,
  };

  if (!progress) return { ...facts, step: null };

  if (wantsCertificate && status && !status.certificateUploaded) {
    return { ...facts, step: stepNumberForConcern(flow, "certificate") };
  }

  return { ...facts, step: restoreComposedStep(flow, progress) };
}

/**
 * Has the identity the later steps are attached to changed?
 *
 * Returning to the identity step and submitting a DIFFERENT cedula or
 * credential re-verifies, and everything collected after it was collected for
 * someone else — so the page clears it (control C-4). Re-submitting the SAME
 * identity is a no-op and must not wipe a photo the applicant already chose.
 *
 * THE CREDENTIAL IS OPTIONAL HERE because not every profession has one. A
 * flow that verifies by document lookup compares cedulas alone, which is the
 * whole of its identity.
 */
export function inviteIdentityChanged(
  previous: { cedula: string; credential?: string } | null,
  next: { cedula: string; credential?: string },
): boolean {
  if (!previous) return false;
  return (
    previous.cedula !== next.cedula ||
    (previous.credential ?? "") !== (next.credential ?? "")
  );
}

/**
 * The step list a profession's applicant walks.
 *
 * ONE STRATEGY KEEPS ITS EXISTING BUNDLING. A poder_judicial profession walks
 * the notary's six steps and a document_lookup one the accountant's four,
 * because those are the flows people are completing today and this change must
 * not reshape them (C-3). The bundling is a property of the FLOW, not of the
 * strategy — which is why it cannot simply be unioned.
 *
 * SEVERAL STRATEGIES GET THE CANONICAL, UNBUNDLED SEQUENCE: one concern per
 * step. Combining two different bundlings is undefined — there is no principled
 * way to merge "phone, coverage and password are one step" with "they are
 * three" — so the composed flow takes the finer cut, which is the only one
 * that can express both. No existing profession is affected, because no
 * existing profession has two.
 */
export function composeSteps(
  strategyNames: readonly string[],
  flowFor: (strategy: string) => readonly ComposedStep[] | undefined,
): ComposedStep[] {
  const unknown = strategyNames.filter((s) => !(s in STRATEGY_REQUIREMENTS));
  if (unknown.length > 0) {
    throw new Error(
      `No client requirements declared for verification strategy ${unknown.join(', ')}. ` +
        `An admin can configure it and the wizard cannot ask for it.`,
    );
  }

  if (strategyNames.length === 1) {
    const existing = flowFor(strategyNames[0]);
    if (existing) return [...existing];
  }

  const required = new Set<RequirementKey>();
  for (const name of strategyNames) {
    for (const key of STRATEGY_REQUIREMENTS[name]) required.add(key);
  }

  const needed = new Set<Concern>(SIGNUP_TAIL);
  for (const key of required) needed.add(CONCERN_FOR_REQUIREMENT[key]);

  return CANONICAL_CONCERNS.filter((concern) => needed.has(concern)).map((concern) => ({
    concerns: [concern],
    labelKey: `auth.acceptProfessionalInvitation.step.${concern}`,
    requirements: [...required].filter((key) => CONCERN_FOR_REQUIREMENT[key] === concern),
  }));
}
